(() => {
  const ROOT_SELECTOR = '[data-kristal-customizer]';
  const PROTECTED_CONTROL_SELECTOR = [
    `${ROOT_SELECTOR} select`,
    `${ROOT_SELECTOR} .kristal-customizer__addon`,
    `${ROOT_SELECTOR} [data-customizer-go]`,
    `${ROOT_SELECTOR} [data-customizer-submit]`,
    `${ROOT_SELECTOR} [data-customizer-file-clear]`,
    `${ROOT_SELECTOR} [data-customizer-tip-toggle]`,
    `${ROOT_SELECTOR} [data-customizer-engraving-chip]`,
    `${ROOT_SELECTOR} [data-customizer-dropzone]`,
    `${ROOT_SELECTOR} .kristal-customizer__details`,
  ].join(', ');
  const FORWARDABLE_CONTROL_SELECTOR = [
    `${ROOT_SELECTOR} select`,
    '.kristal-customizer__addon',
    '[data-customizer-go]',
    '[data-customizer-submit]',
    '[data-customizer-file-clear]',
    '[data-customizer-tip-toggle]',
    '[data-customizer-engraving-chip]',
    '[data-customizer-dropzone]',
    '[data-customizer-qty-minus]',
    '[data-customizer-qty-plus]',
  ].join(', ');

  const getRoot = (target) => target && target.closest ? target.closest(ROOT_SELECTOR) : null;

  const stopThemeHandlers = (event) => {
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }
  };

  const pointIsInside = (event, element) => {
    if (!event || typeof event.clientX !== 'number' || typeof event.clientY !== 'number') return false;

    const rect = element.getBoundingClientRect();
    return event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
  };

  const formatMoney = (cents) => {
    const amount = Number(cents) || 0;

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(amount, window.money_format || window.Shopify.money_format || '${{amount}}');
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR',
      }).format(amount / 100);
    } catch (error) {
      return `${(amount / 100).toFixed(2)}`;
    }
  };

  const customizerVariants = (root) => {
    if (root._kristalVariants) return root._kristalVariants;

    const source = root.querySelector('[data-customizer-variants]');
    try {
      root._kristalVariants = source ? JSON.parse(source.textContent || '[]') : [];
    } catch (error) {
      root._kristalVariants = [];
    }

    return root._kristalVariants;
  };

  const productVariantInput = (root) => {
    const productView = root?.closest('.productView, [id^="ProductSection-"]') || document;
    const inputs = Array.from(productView.querySelectorAll('form input[name="id"], form select[name="id"]'))
      .filter((input) => !root.contains(input) && !input.disabled);

    return inputs[0] || null;
  };

  const customizerVariantChoices = (root) => Array.from(root?.querySelectorAll?.('[data-customizer-variant-choice]') || []);

  const setCustomizerVariantChoice = (root, variantId) => {
    if (!root || !variantId) return;

    customizerVariantChoices(root).forEach((input) => {
      const isSelected = String(input.value) === String(variantId);
      input.checked = isSelected;
      const card = input.closest('.kristal-customizer__variant-card, .kristal-customizer__addon');
      if (card) card.classList.toggle('is-selected', isSelected);
    });
  };

  const syncPageVariantInput = (root, variantId) => {
    const pageInput = productVariantInput(root);
    if (!pageInput || !variantId || String(pageInput.value) === String(variantId)) return;

    pageInput.value = String(variantId);
    pageInput.dispatchEvent(new Event('input', { bubbles: true }));
    pageInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const syncVariantFromPage = (root) => {
    const pageInput = productVariantInput(root);
    const hiddenVariant = root.querySelector('[data-customizer-variant-input]');
    const selectedChoice = root.querySelector('[data-customizer-variant-choice]:checked');
    const variantId = pageInput?.value || selectedChoice?.value || new URLSearchParams(window.location.search).get('variant') || hiddenVariant?.value;
    const variant = customizerVariants(root).find((item) => String(item.id) === String(variantId));
    const quantityInput = root.querySelector('[data-customizer-quantity]');
    const pageQuantity = productQuantityInput(root);
    const basePrice = Number(variant?.price || pageQuantity?.dataset.price || quantityInput?.dataset.customizerUnitPrice || 0);
    const selectedOption = root.querySelector('[data-customizer-selected-option]');
    const selectedModelInput = root.querySelector('[data-customizer-selected-model]');

    if (hiddenVariant && variantId) hiddenVariant.value = variantId;
    if (selectedOption && variant?.title) selectedOption.value = variant.title;
    if (selectedModelInput && variant?.title) selectedModelInput.value = variant.title;
    if (quantityInput) quantityInput.dataset.customizerUnitPrice = String(basePrice);
    if (variantId) setCustomizerVariantChoice(root, variantId);

    return { id: variantId, title: variant?.title, price: basePrice };
  };

  const selectCustomizerVariant = (root, variantId) => {
    if (!root || !variantId) return;

    const variant = customizerVariants(root).find((item) => String(item.id) === String(variantId));
    if (!variant) return;

    const hiddenVariant = root.querySelector('[data-customizer-variant-input]');
    const selectedOption = root.querySelector('[data-customizer-selected-option]');
    const selectedModelInput = root.querySelector('[data-customizer-selected-model]');
    const quantityInput = root.querySelector('[data-customizer-quantity]');

    if (hiddenVariant) hiddenVariant.value = String(variant.id);
    if (selectedOption) selectedOption.value = variant.title || '';
    if (selectedModelInput) selectedModelInput.value = variant.title || '';
    if (quantityInput) quantityInput.dataset.customizerUnitPrice = String(Number(variant.price || 0));

    setCustomizerVariantChoice(root, variant.id);
    syncPageVariantInput(root, variant.id);
    updateTotals(root);
  };

  const productQuantityInput = (root) => {
    const productId = root?.dataset.productId;
    const productView = root?.closest('.productView, [id^="ProductSection-"]') || document;
    const scopedInputs = Array.from(productView.querySelectorAll('input[name="quantity"][data-price]'))
      .filter((input) => !root.contains(input));
    const matched = scopedInputs.find((input) => !productId || input.dataset.product === productId);

    return matched || scopedInputs[0] || null;
  };

  const readQuantity = (root) => {
    const hiddenInput = root.querySelector('[data-customizer-quantity]');
    const pageInput = productQuantityInput(root);
    const source = pageInput || hiddenInput;
    const value = Math.max(1, Number(source?.value) || 1);
    if (hiddenInput) hiddenInput.value = String(value);
    if (pageInput && Number(pageInput.value) !== value) pageInput.value = String(value);
    return value;
  };

  const readSelectedAddonTotal = (root) => Array.from(root.querySelectorAll('[data-customizer-addon]:checked'))
    .reduce((sum, input) => sum + (Number(input.dataset.customizerAddonPrice) || 0), 0);

  const addonKind = (label = '', title = '') => {
    const value = `${label} ${title}`.toLowerCase();
    if (value.includes('warranty')) return 'warranty';
    if (value.includes('figure') || value.includes('3d')) return 'figures';
    if (value.includes('base')) return 'light-base';
    return 'add-on';
  };

  const selectedPaidAddons = (root) => Array.from(root.querySelectorAll('[data-customizer-addon]:checked'))
    .map((input) => ({
      input,
      label: input.value || input.closest('.kristal-customizer__addon')?.textContent?.trim() || 'Add-on',
      price: Number(input.dataset.customizerAddonPrice) || 0,
      priceText: input.dataset.customizerAddonPriceText || '',
      variantId: input.dataset.customizerAddonVariantId || '',
      kind: addonKind(input.value, input.closest('.kristal-customizer__addon')?.textContent || ''),
    }))
    .filter((addon) => addon.price > 0);

  const deleteFormDataNames = (body, names) => {
    names.forEach((name) => body.delete(name));
  };

  const pruneMainProductAddonProperties = (body, paidAddons = []) => {
    deleteFormDataNames(body, [
      'properties[Estimated add-ons]',
      'properties[Estimated add-on total]',
      'properties[Estimated order total]',
      'properties[Paid add-ons]',
      'properties[Paid add-ons total]',
      'properties[Customization notes]',
    ]);

    paidAddons.forEach((addon) => {
      if (addon.kind === 'figures') body.delete('properties[3D figures request]');
      if (addon.kind === 'warranty') body.delete('properties[Warranty request]');
      if (addon.kind === 'light-base') body.delete('properties[Light base request]');
    });
  };

  const cartAddJsonUrl = (form) => {
    const url = window.routes?.cart_add_url || form?.action || '/cart/add';
    return /\.js($|\?)/.test(url) ? url : url.replace(/(\?.*)?$/, '.js$1');
  };

  const cartAddNativeUrl = (form) => {
    const url = window.routes?.cart_add_url || form?.action || '/cart/add';
    return url.replace(/\/cart\/add\.js(\?.*)?$/, '/cart/add$1');
  };

  const cleanHtmlText = (value) => String(value || '')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const parseCartResponse = async (response) => {
    const contentType = response.headers?.get?.('content-type') || '';
    if (contentType.includes('json')) {
      return response.json().catch(() => ({}));
    }

    const text = await response.text().catch(() => '');
    const description = cleanHtmlText(text);
    return description ? { description } : {};
  };

  const cartErrorMessage = (state, fallback) => {
    const message = cleanHtmlText(state?.description || state?.message || fallback || 'Could not add this product to cart.');
    if (/cannot be added|can't add|not enough|sold out|inventory|stock/i.test(message)) {
      return 'The selected quantity is not available yet. Please try a smaller quantity or contact us for a custom order.';
    }
    return message || fallback || 'Could not add this product to cart.';
  };

  const setFormData = (body, name, value) => {
    if (typeof body.set === 'function') {
      body.set(name, value);
      return;
    }

    body.delete(name);
    body.append(name, value);
  };

  const cleanPropertyValue = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

  const fieldValue = (root, selector) => cleanPropertyValue(root.querySelector(selector)?.value || '');

  const checkedValue = (root, name) => cleanPropertyValue(root.querySelector(`input[name="${name}"]:checked`)?.value || '');

  const updateCustomizerProperties = (root) => {
    if (!root) return;

    const variant = syncVariantFromPage(root);
    const selectedOption = cleanPropertyValue(root.querySelector('[data-customizer-selected-option]')?.value || variant.title || '');
    const file = requiredFileInput(root)?.files?.[0] || null;
    const selectedModelInput = root.querySelector('[data-customizer-selected-model]');
    const uploadNameInput = root.querySelector('[data-customizer-upload-file-name-input]');
    const notesSummaryInput = root.querySelector('[data-customizer-notes-summary]');

    if (selectedModelInput) selectedModelInput.value = selectedOption;
    if (uploadNameInput) uploadNameInput.value = file ? file.name : '';

    if (!notesSummaryInput) return;

    const rows = [
      ['Selected model', selectedOption],
      ['Photo upload file', file ? file.name : ''],
      ['Engraving text', fieldValue(root, '[data-customizer-engraving-text]')],
      ['Engraving font', fieldValue(root, '[data-customizer-engraving-font]')],
      ['Engraving placement', fieldValue(root, '[data-customizer-engraving-placement]')],
      ['Number of people', fieldValue(root, 'select[name="properties[Number of people]"]')],
      ['3D figures request', checkedValue(root, 'properties[3D figures request]')],
      ['Image edit preference', fieldValue(root, 'select[name="properties[Image edit preference]"]')],
      ['Image and engraving notes', fieldValue(root, 'textarea[name="properties[Image and engraving notes]"]')],
      ['Light base request', checkedValue(root, 'properties[Light base request]')],
      ['Warranty request', checkedValue(root, 'properties[Warranty request]')],
      ['Production notes', fieldValue(root, 'textarea[name="properties[Production notes]"]')],
    ].filter(([, value]) => value);

    notesSummaryInput.value = rows.map(([label, value]) => `${label}: ${value}`).join(' | ');
  };

  const customizerForProductForm = (form) => {
    if (!form || form.matches?.(ROOT_SELECTOR) || getRoot(form) || !form.querySelector('[name="id"]')) return null;

    const productView = form.closest('.productView, [id^="ProductSection-"]') || document;
    return productView.querySelector(ROOT_SELECTOR);
  };

  const requestSections = (cartNotification) => (
    cartNotification?.getSectionsToRender ? cartNotification.getSectionsToRender().map((section) => section.id).join(',') : ''
  );

  const addAddonLineItems = async ({ form, root, addons, quantity, bundleId, sections }) => {
    if (!addons.length) return null;

    const missingVariant = addons.find((addon) => !addon.variantId);
    if (missingVariant) {
      throw new Error(`${missingVariant.label} has no Shopify add-on variant configured.`);
    }

    const selectedOption = root.querySelector('[data-customizer-selected-option]')?.value || syncVariantFromPage(root).title || '';
    const productTitle = root.dataset.productTitle || document.querySelector('.productView-title')?.textContent?.trim() || 'Customized product';

    const payload = {
      items: addons.map((addon) => ({
        id: addon.variantId,
        quantity,
        properties: {
          'For product': productTitle,
          'Selected product option': selectedOption,
          'Add-on selection': `${addon.label}${addon.priceText ? ` (${addon.priceText})` : ''}`,
          '_Customization bundle': bundleId,
          '_Add-on kind': addon.kind,
          '_Bundled main product': productTitle,
        },
      })),
    };

    if (sections) {
      payload.sections = sections;
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch(cartAddJsonUrl(form), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });
    const parsedState = await parseCartResponse(response);

    if (!response.ok || parsedState.status) {
      throw new Error(cartErrorMessage(parsedState, 'Add-ons could not be added to cart.'));
    }

    return parsedState;
  };

  const updateTotals = (root) => {
    const variant = syncVariantFromPage(root);
    const quantity = readQuantity(root);
    const basePrice = Number(variant.price || root.querySelector('[data-customizer-quantity]')?.dataset.customizerUnitPrice || 0);
    const addonPrice = readSelectedAddonTotal(root);
    const unitPrice = basePrice + addonPrice;
    const total = formatMoney(unitPrice * quantity);
    const addonTotal = formatMoney(addonPrice);
    const breakdown = `${formatMoney(basePrice)} base + ${addonTotal} add-ons x ${quantity}`;

    root.querySelectorAll('[data-customizer-total], [data-customizer-submit-total]').forEach((element) => {
      element.textContent = total;
    });

    root.querySelectorAll('[data-customizer-total-breakdown]').forEach((element) => {
      element.textContent = breakdown;
    });

    const addonInput = root.querySelector('[data-customizer-addon-total-input]');
    if (addonInput) addonInput.value = addonTotal;

    const orderInput = root.querySelector('[data-customizer-order-total-input]');
    if (orderInput) orderInput.value = total;

    const quantityInput = root.querySelector('[data-customizer-quantity]');
    if (quantityInput) quantityInput.dataset.customizerUnitPrice = String(basePrice);

    updateCustomizerProperties(root);
  };

  const setMessage = (root, text, isError = false) => {
    const message = root.querySelector('[data-customizer-message]');
    if (!message) return;

    message.hidden = !text;
    message.textContent = text || '';
    message.classList.toggle('is-error', Boolean(isError));
  };

  const setSubmitLoading = (root, isLoading) => {
    const button = root.querySelector('[data-customizer-submit]');
    if (!button) return;

    button.disabled = Boolean(isLoading);
    button.classList.toggle('is-loading', Boolean(isLoading));
  };

  const requiredUploadMessage = 'Please upload a photo first. After you choose it, the add-ons step will open automatically.';

  const requiredFileInput = (root) => root?.querySelector('[data-customizer-file]');

  const hasRequiredUpload = (root) => {
    const input = requiredFileInput(root);
    return !input || Boolean(input.files && input.files.length);
  };

  const setUploadRequirementState = (root, isMissing) => {
    const input = requiredFileInput(root);
    const dropzone = input?.closest('[data-customizer-dropzone]');
    const uploadMessage = root?.querySelector('[data-customizer-upload-message]');

    root?.classList.toggle('is-missing-required-upload', Boolean(isMissing));
    dropzone?.classList.toggle('is-required-error', Boolean(isMissing));

    if (uploadMessage) {
      uploadMessage.hidden = !isMissing;
      uploadMessage.textContent = isMissing ? requiredUploadMessage : '';
    }

    if (input) {
      input.setAttribute('aria-invalid', String(Boolean(isMissing)));
      input.setCustomValidity(isMissing ? requiredUploadMessage : '');
    }
  };

  const requireUploadBeforeContinue = (root, pendingStep = '') => {
    if (hasRequiredUpload(root)) {
      setUploadRequirementState(root, false);
      return true;
    }

    setStep(root, '1');
    setUploadRequirementState(root, true);
    setMessage(root, requiredUploadMessage, true);

    if (pendingStep) {
      root.dataset.kristalPendingStepAfterUpload = String(pendingStep);
    }

    const upload = root.querySelector('[data-customizer-dropzone]');
    if (upload) {
      const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      upload.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
    }

    const fileInput = requiredFileInput(root);
    if (fileInput && typeof fileInput.click === 'function') {
      try {
        fileInput.click();
      } catch (error) {
        window.setTimeout(() => fileInput.click(), 0);
      }
    }

    return false;
  };

  const validateRequiredFields = (root, pendingStep = '') => {
    if (!requireUploadBeforeContinue(root, pendingStep)) return false;

    const invalidField = Array.from(root.querySelectorAll('[required], [aria-required="true"]'))
      .filter((field) => !field.disabled && field.type !== 'file')
      .find((field) => typeof field.checkValidity === 'function' && !field.checkValidity());

    if (!invalidField) {
      setMessage(root, '');
      return true;
    }

    const panel = invalidField.closest('[data-customizer-panel]');
    if (panel?.dataset.customizerPanel) setStep(root, panel.dataset.customizerPanel);
    setMessage(root, invalidField.validationMessage || 'Please complete the required fields before continuing.', true);
    invalidField.focus({ preventScroll: false });
    return false;
  };

  const ensureHiddenInput = (form, name, value) => {
    let input = Array.from(form.querySelectorAll('input[type="hidden"]')).find((field) => field.name === name);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.disabled = false;
    input.value = value;
    return input;
  };

  const prepareNativeSubmit = ({ form, root, paidAddons, quantity, bundleId }) => {
    const disabledInputs = [];
    const disableForNativeSubmit = (input) => {
      if (!input || input.disabled) return;
      input.disabled = true;
      disabledInputs.push(input);
    };

    ensureHiddenInput(form, 'quantity', String(quantity));
    ensureHiddenInput(form, 'properties[_Customization bundle]', bundleId);

    [
      '[data-customizer-addons-summary]',
      '[data-customizer-addon-total-input]',
      '[data-customizer-order-total-input]',
      '[data-customizer-notes-summary]',
    ].forEach((selector) => disableForNativeSubmit(root.querySelector(selector)));

    paidAddons.forEach((addon) => disableForNativeSubmit(addon.input));

    return () => {
      disabledInputs.forEach((input) => {
        input.disabled = false;
      });
    };
  };

  const submitNativeCustomizer = async ({ form, root, paidAddons, quantity, bundleId }) => {
    const restoreInputs = prepareNativeSubmit({ form, root, paidAddons, quantity, bundleId });

    try {
      await addAddonLineItems({
        form,
        root,
        addons: paidAddons,
        quantity,
        bundleId,
        sections: '',
      });

      form.action = cartAddNativeUrl(form);
      form.method = 'post';
      form.enctype = 'multipart/form-data';
      root.dataset.kristalNativeSubmitting = 'true';

      if (window.HTMLFormElement?.prototype?.submit) {
        window.HTMLFormElement.prototype.submit.call(form);
      } else {
        form.submit();
      }
    } catch (error) {
      restoreInputs();
      delete root.dataset.kristalNativeSubmitting;
      throw error;
    }
  };

  const submitCustomizer = async (root) => {
    const form = root.matches('form') ? root : root.querySelector('form');
    if (!form) return;

    if (!validateRequiredFields(root, '2')) return;

    const cartNotification = document.querySelector('cart-notification');
    updateCustomizerProperties(root);
    const body = new FormData(form);
    const quantity = readQuantity(root);
    const paidAddons = selectedPaidAddons(root);
    const bundleId = `kristal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const sections = requestSections(cartNotification);
    const hasFileUpload = Array.from(root.querySelectorAll('input[type="file"]')).some((input) => input.files && input.files.length);

    form.querySelectorAll('input[type="file"]').forEach((input) => {
      if (!input.files || !input.files.length) body.delete(input.name);
    });

    setFormData(body, 'quantity', String(quantity));
    setFormData(body, 'properties[_Customization bundle]', bundleId);
    pruneMainProductAddonProperties(body, paidAddons);

    if (sections && !paidAddons.length) {
      body.append('sections', sections);
      body.append('sections_url', window.location.pathname);
    }

    setSubmitLoading(root, true);
    setMessage(root, '');

    try {
      if (hasFileUpload) {
        await submitNativeCustomizer({ form, root, paidAddons, quantity, bundleId });
        return;
      }

      if (cartNotification?.setActiveElement) {
        cartNotification.setActiveElement(document.activeElement);
      }

      const response = await fetch(cartAddJsonUrl(form), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body,
      });
      let parsedState = await parseCartResponse(response);

      if (!response.ok || parsedState.status) {
        throw new Error(cartErrorMessage(parsedState, 'Could not add this product to cart.'));
      }

      const addonState = await addAddonLineItems({
        form,
        root,
        addons: paidAddons,
        quantity,
        bundleId,
        sections,
      });

      if (addonState) parsedState = addonState;

      if (cartNotification?.renderContents) {
        cartNotification.renderContents(parsedState);
        setMessage(root, paidAddons.length ? 'Product and selected add-ons were added to cart.' : 'Added to cart.');
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
      }
    } catch (error) {
      setMessage(root, error.message || 'Could not add this product to cart. Please check the required fields and try again.', true);
    } finally {
      setSubmitLoading(root, false);
    }
  };

  const updateThemeProductSubtotal = (container) => {
    if (!window.subtotal || !window.subtotal.show) return;

    const form = container?.closest?.('form') || container?.querySelector?.('form') || container;
    if (!form || form.matches?.(ROOT_SELECTOR)) return;

    const quantityInput = form.querySelector('input[name="quantity"][data-price]');
    const addButton = form.querySelector('[name="add"]');
    if (!quantityInput || !addButton) return;

    const quantity = Math.max(1, Number(quantityInput.value) || 1);
    const unitPrice = Number(quantityInput.dataset.price || 0);
    const subtotal = formatMoney(unitPrice * quantity);

    if (window.subtotal.style === '1') {
      const productView = form.closest('.productView') || document;
      const subtotalElement = productView.querySelector('.productView-subtotal .money, .productView-subtotal .money-subtotal');
      if (subtotalElement) subtotalElement.textContent = subtotal;
      return;
    }

    if (window.subtotal.style === '2' && window.subtotal.text) {
      addButton.textContent = window.subtotal.text.replace('[value]', subtotal);
    }
  };

  const updateCustomizerForProductQuantity = (quantityInput) => {
    const productView = quantityInput?.closest?.('.productView, [id^="ProductSection-"]') || document;
    const roots = productView.querySelectorAll(ROOT_SELECTOR);

    roots.forEach((root) => {
      const hiddenInput = root.querySelector('[data-customizer-quantity]');
      if (hiddenInput) hiddenInput.value = String(Math.max(1, Number(quantityInput.value) || 1));
      updateTotals(root);
    });
  };

  const quantityDirection = (button) => {
    if (!button || !button.matches) return 0;
    if (button.matches('.plus, [name="plus"], [data-plus-quantity], [data-plus-quantity-cart], [data-plus-quantity-quickshop], [data-customizer-qty-plus]')) return 1;
    if (button.matches('.minus, [name="minus"], [data-minus-quantity], [data-minus-quantity-cart], [data-minus-quantity-quickshop], [data-customizer-qty-minus]')) return -1;
    return 0;
  };

  const quantityInputFromButton = (button) => {
    const container = button?.closest?.('quantity-input, cart-update-quantity, quickshop-update-quantity, .productView-quantity, .quantity__container, .cart-item-qty');
    return container?.querySelector?.('input.quantity__input, input.quantity, input[name="quantity"], input[type="number"]') || null;
  };

  const minQuantityValue = (input) => {
    const value = Number(input?.getAttribute?.('min'));
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  const dispatchQuantityEvents = (input) => {
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));

    if (input.matches?.('input[name="quantity"][data-price]')) {
      updateThemeProductSubtotal(input);
      updateCustomizerForProductQuantity(input);
    }
  };

  const scheduleQuantityFallback = (button) => {
    const direction = quantityDirection(button);
    const input = quantityInputFromButton(button);
    if (!direction || !input || input.disabled) return;

    const minimum = minQuantityValue(input);
    const before = Math.max(minimum, Number(input.value) || minimum);

    window.setTimeout(() => {
      const after = Number(input.value) || 0;
      let nextValue = null;

      if (direction < 0) {
        if (after < minimum) {
          nextValue = minimum;
        } else if (before > minimum && after >= before) {
          nextValue = before - 1;
        }
      } else if (direction > 0 && after <= before) {
        nextValue = before + 1;
      }

      if (nextValue === null) {
        if (input.matches?.('input[name="quantity"][data-price]')) {
          updateThemeProductSubtotal(input);
          updateCustomizerForProductQuantity(input);
        }
        return;
      }

      input.value = String(Math.max(minimum, nextValue));
      dispatchQuantityEvents(input);
    }, 80);
  };

  const updateCustomizerForProductVariant = (variantInput) => {
    const productView = variantInput?.closest?.('.productView, [id^="ProductSection-"]') || document;
    const roots = productView.querySelectorAll(ROOT_SELECTOR);

    roots.forEach((root) => {
      syncVariantFromPage(root);
      updateTotals(root);
    });
  };

  const blockOverlayNavigation = (event) => {
    if (!event || !document.querySelector(ROOT_SELECTOR)) return false;

    const target = event.target;
    const roots = document.querySelectorAll(ROOT_SELECTOR);

    for (const root of roots) {
      if (root.contains(target)) {
        return false;
      }

      if (pointIsInside(event, root)) {
        const control = customizerControlFromPoint(event, root);
        if (!control) continue;

        const addonInput = control.matches('.kristal-customizer__addon')
          ? control.querySelector('[data-customizer-addon]')
          : null;

        event.preventDefault();
        stopThemeHandlers(event);

        if (addonInput) {
          selectAddonInput(root, addonInput);
        } else if (event.type === 'click') {
          forwardCustomizerControl(control);
        }

        return true;
      }
    }

    return false;
  };

  const customizerElementFromPoint = (event, selector) => {
    if (!event || typeof event.clientX !== 'number' || typeof event.clientY !== 'number' || !document.elementsFromPoint) return null;

    for (const element of document.elementsFromPoint(event.clientX, event.clientY)) {
      const match = element.closest?.(selector);
      if (match && getRoot(match)) return match;
    }

    return null;
  };

  const customizerControlFromPoint = (event, root) => {
    const control = customizerElementFromPoint(event, FORWARDABLE_CONTROL_SELECTOR);
    return control && root?.contains?.(control) ? control : null;
  };

  let forwardingCustomizerControl = false;

  const forwardCustomizerControl = (control) => {
    if (!control || forwardingCustomizerControl) return false;

    if (control.matches?.('select')) {
      openCustomizerSelect(control);
      return true;
    }

    forwardingCustomizerControl = true;
    window.setTimeout(() => {
      try {
        control.click();
      } finally {
        forwardingCustomizerControl = false;
      }
    }, 0);

    return true;
  };

  const selectAddonInput = (root, addonInput) => {
    if (!root || !addonInput || addonInput.disabled) return false;

    if (!addonInput.checked) {
      addonInput.checked = true;
      addonInput.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      syncAddon(root, addonInput);
      updateTotals(root);
    }

    return true;
  };

  const blockExternalOverlayNavigation = (event) => {
    if (!event || !document.querySelector(ROOT_SELECTOR)) return false;

    const target = event.target;
    const roots = document.querySelectorAll(ROOT_SELECTOR);

    for (const root of roots) {
      if (root.contains(target)) return false;

      if (pointIsInside(event, root)) {
        const control = customizerControlFromPoint(event, root);
        if (!control) continue;

        const addonInput = control.matches('.kristal-customizer__addon')
          ? control.querySelector('[data-customizer-addon]')
          : null;

        event.preventDefault();
        stopThemeHandlers(event);

        if (addonInput) {
          selectAddonInput(root, addonInput);
        } else if (event.type === 'click') {
          forwardCustomizerControl(control);
        }

        return true;
      }
    }

    return false;
  };

  const setStep = (root, step) => {
    const nextStep = String(step || '1');

    root.querySelectorAll('[data-customizer-panel]').forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.customizerPanel === nextStep);
    });

    root.querySelectorAll('.kristal-customizer__tab[data-customizer-go]').forEach((button) => {
      button.classList.toggle('is-active', button.dataset.customizerGo === nextStep);
    });

    const panel = root.querySelector(`[data-customizer-panel="${nextStep}"]`);
    if (panel) {
      const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      panel.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
    }
  };

  const openCustomizerSelect = (select) => {
    if (!select || select.disabled) return;

    try {
      select.focus({ preventScroll: true });
    } catch (error) {
      select.focus();
    }

    if (typeof select.showPicker === 'function') {
      try {
        select.showPicker();
      } catch (error) {
        // Some browsers only allow showPicker from specific trusted events.
      }
    }
  };

  const closeCustomizerSelect = (select) => {
    if (!select || !getRoot(select)) return;

    window.setTimeout(() => {
      try {
        select.blur();
      } catch (error) {
        // Native select popups do not expose a close API; blur is the safest cross-browser fallback.
      }
    }, 0);

    window.setTimeout(() => {
      if (document.activeElement === select) {
        try {
          select.blur();
        } catch (error) {
          // Keep this defensive only; the visible design should not change.
        }
      }
    }, 80);
  };

  let lastStepActivationAt = 0;

  const handleStepButtonActivation = (event) => {
    const goButton = event.target.closest(`${ROOT_SELECTOR} [data-customizer-go]`);
    if (!goButton) return false;

    const root = getRoot(goButton);
    if (!root) return false;

    const now = Date.now();
    if (event.type === 'click' && now - lastStepActivationAt < 450) {
      event.preventDefault();
      stopThemeHandlers(event);
      return true;
    }

    lastStepActivationAt = now;
    event.preventDefault();
    stopThemeHandlers(event);
    initializeRoot(root);

    const nextStep = goButton.dataset.customizerGo || '1';
    if (Number(nextStep) >= 2 && !requireUploadBeforeContinue(root, nextStep)) {
      return true;
    }

    setStep(root, nextStep);
    return true;
  };

  const handleDetailsSummaryActivation = (event) => {
    const summary = event.target.closest(`${ROOT_SELECTOR} .kristal-customizer__details summary`);
    if (!summary) return false;

    const details = summary.closest('details');
    if (!details) return false;

    event.preventDefault();
    stopThemeHandlers(event);
    details.open = !details.open;
    details.classList.toggle('is-open', details.open);
    summary.setAttribute('aria-expanded', String(details.open));
    return true;
  };

  const handlePassiveDetailsActivation = (event) => {
    const infoArea = event.target.closest(`${ROOT_SELECTOR} .kristal-customizer__process, ${ROOT_SELECTOR} .kristal-customizer__details details p`);
    if (!infoArea) return false;

    const interactive = event.target.closest('a, button, input, select, textarea, summary, label, [role="button"]');
    if (interactive && infoArea.contains(interactive)) return false;

    event.preventDefault();
    stopThemeHandlers(event);
    return true;
  };

  const syncAddon = (root, input) => {
    root.querySelectorAll('[data-customizer-addon]').forEach((groupInput) => {
      if (groupInput.name !== input.name) return;
      const label = groupInput.closest('.kristal-customizer__addon');
      if (label) label.classList.toggle('is-selected', groupInput.checked);
    });

    const figureInput = root.querySelector('input[name="properties[3D figures request]"]:checked');
    const figures = figureInput?.value || 'Make The Crystal in 2D';
    const figurePrice = figureInput?.dataset.customizerAddonPriceText || '';
    const baseInput = root.querySelector('input[name="properties[Light base request]"]:checked');
    const base = baseInput?.value || 'No Base';
    const basePrice = baseInput?.dataset.customizerAddonPriceText || '';
    const warrantyInput = root.querySelector('input[name="properties[Warranty request]"]:checked');
    const warranty = warrantyInput?.value || 'No warranty';
    const warrantyPrice = warrantyInput?.dataset.customizerAddonPriceText || '';
    const summary = root.querySelector('[data-customizer-addons-summary]');
    if (summary) summary.value = `${figures}${figurePrice ? ` (${figurePrice})` : ''} / ${base}${basePrice ? ` (${basePrice})` : ''} / ${warranty}${warrantyPrice ? ` (${warrantyPrice})` : ''}`;
  };

  const updateEngravingPreview = (root) => {
    if (!root) return;

    const textInput = root.querySelector('[data-customizer-engraving-text]');
    const count = root.querySelector('[data-customizer-engraving-count]');
    const preview = root.querySelector('[data-customizer-engraving-preview]');
    const previewMeta = root.querySelector('[data-customizer-engraving-preview-meta]');
    const font = root.querySelector('[data-customizer-engraving-font]');
    const placement = root.querySelector('[data-customizer-engraving-placement]');
    const text = (textInput?.value || '').trim();

    if (count && textInput) {
      count.textContent = String(textInput.value.length);
    }

    if (preview) {
      preview.textContent = text || 'Your message appears here';
      preview.classList.toggle('is-empty', !text);
    }

    if (previewMeta) {
      previewMeta.textContent = `${font?.value || 'Designer choice'} / ${placement?.value || 'Designer choice'}`;
    }

    updateCustomizerProperties(root);
  };

  const updateFileState = (root, fileInput) => {
    if (!root || !fileInput) return;

    const file = fileInput.files && fileInput.files.length ? fileInput.files[0] : null;
    const fileName = root.querySelector('[data-customizer-file-name]');
    const preview = root.querySelector('[data-customizer-file-preview]');
    const previewImage = root.querySelector('[data-customizer-preview-image]');
    const fileStatus = root.querySelector('[data-customizer-file-status]');

    root.classList.toggle('has-uploaded-image', Boolean(file));
    setUploadRequirementState(root, false);

    if (file) {
      setMessage(root, '');
      const pendingStep = root.dataset.kristalPendingStepAfterUpload;
      if (pendingStep) {
        delete root.dataset.kristalPendingStepAfterUpload;
        setStep(root, pendingStep);
      }
    }

    if (fileName) {
      fileName.textContent = file ? file.name : 'No image selected';
    }

    if (fileStatus) {
      fileStatus.textContent = file ? 'Image ready for engraving' : 'No image selected';
    }

    updateCustomizerProperties(root);

    if (!preview || !previewImage) return;

    if (!file) {
      preview.hidden = true;
      previewImage.removeAttribute('src');
      return;
    }

    preview.hidden = false;

    if (file.type && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        previewImage.src = reader.result;
      }, { once: true });
      reader.readAsDataURL(file);
    }
  };

  const resetRadioGroups = (root, selector) => {
    const groups = new Map();

    root.querySelectorAll(selector).forEach((input) => {
      if (!groups.has(input.name)) groups.set(input.name, []);
      groups.get(input.name).push(input);
    });

    groups.forEach((inputs) => {
      const defaultInput = inputs.find((input) => input.defaultChecked && !input.disabled)
        || inputs.find((input) => !input.disabled && Number(input.dataset.customizerAddonPrice || 0) === 0)
        || inputs.find((input) => !input.disabled);

      inputs.forEach((input) => {
        input.checked = input === defaultInput;
      });

      if (defaultInput?.matches?.('[data-customizer-addon]')) {
        syncAddon(root, defaultInput);
      }
    });
  };

  const resetRootState = (root) => {
    if (!root) return;

    delete root.dataset.kristalPendingStepAfterUpload;
    delete root.dataset.kristalNativeSubmitting;

    const pageQuantity = productQuantityInput(root);
    const customizerQuantity = root.querySelector('[data-customizer-quantity]');
    [pageQuantity, customizerQuantity].forEach((input) => {
      if (!input || input.disabled) return;
      input.value = String(minQuantityValue(input));
    });
    if (pageQuantity) updateThemeProductSubtotal(pageQuantity);

    const defaultVariant = customizerVariantChoices(root).find((input) => input.defaultChecked && !input.disabled)
      || customizerVariantChoices(root).find((input) => !input.disabled);
    if (defaultVariant) {
      selectCustomizerVariant(root, defaultVariant.value);
    }

    resetRadioGroups(root, '[data-customizer-addon]');

    root.querySelectorAll('select').forEach((select) => {
      select.selectedIndex = 0;
      closeCustomizerSelect(select);
    });

    root.querySelectorAll('textarea').forEach((textarea) => {
      textarea.value = '';
    });

    root.querySelectorAll('[data-customizer-file]').forEach((fileInput) => {
      fileInput.value = '';
      updateFileState(root, fileInput);
    });

    root.querySelectorAll('[data-customizer-tip-box]').forEach((box) => {
      box.hidden = true;
      box.classList.remove('is-open');
    });
    root.querySelectorAll('[data-customizer-tip-toggle]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
    root.querySelectorAll('.kristal-customizer__details details').forEach((details) => {
      details.open = false;
      details.classList.remove('is-open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });

    setUploadRequirementState(root, false);
    setSubmitLoading(root, false);
    setMessage(root, '');
    updateEngravingPreview(root);
    updateTotals(root);
    setStep(root, '1');
  };

  const resetAllRoots = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(resetRootState);
  };

  const isHistoryRestore = (event) => {
    if (event?.persisted) return true;

    const navigationEntry = performance.getEntriesByType?.('navigation')?.[0];
    if (navigationEntry?.type === 'back_forward') return true;

    return window.performance?.navigation?.type === 2;
  };

  const initializeRoot = (root) => {
    if (!root || root.dataset.kristalBound === 'true') return;
    root.dataset.kristalBound = 'true';

    root.querySelectorAll('[data-customizer-addon]:checked').forEach((input) => syncAddon(root, input));

    const fileInput = root.querySelector('[data-customizer-file]');
    if (fileInput) updateFileState(root, fileInput);

    updateEngravingPreview(root);
    updateTotals(root);

    if (!root.querySelector('[data-customizer-panel].is-active')) {
      setStep(root, '1');
    }
  };

  const initializeAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initializeRoot);
  };

  document.addEventListener('click', (event) => {
    if (blockOverlayNavigation(event)) return;

    if (handleDetailsSummaryActivation(event)) return;

    if (handlePassiveDetailsActivation(event)) return;

    if (handleStepButtonActivation(event)) return;

    const customizerSelect = event.target.closest(`${ROOT_SELECTOR} select`);
    if (customizerSelect) {
      event.stopPropagation();
      return;
    }

    const inputShell = event.target.closest(`${ROOT_SELECTOR} .kristal-customizer__input-shell`);
    if (inputShell) {
      const textarea = inputShell.querySelector('textarea');
      if (textarea && event.target !== textarea) {
        event.preventDefault();
        stopThemeHandlers(event);
        textarea.focus({ preventScroll: true });
        return;
      }
    }

    const addonCard = event.target.closest('.kristal-customizer__addon');
    if (addonCard) {
      const root = getRoot(addonCard);
      const addonInput = addonCard.querySelector('[data-customizer-addon]');
      if (root && addonInput && !addonInput.disabled) {
        event.preventDefault();
        stopThemeHandlers(event);
        selectAddonInput(root, addonInput);
        return;
      }
    }

    const uploadTrigger = event.target.closest('.kristal-customizer__upload-label, [data-customizer-dropzone]');
    if (uploadTrigger && !event.target.matches('[data-customizer-file]') && !event.target.closest('[data-customizer-file-clear], [data-customizer-file-preview]')) {
      const root = getRoot(uploadTrigger);
      const fileInput = root ? root.querySelector('[data-customizer-file]') : null;
      if (root && fileInput) {
        event.preventDefault();
        stopThemeHandlers(event);
        fileInput.click();
        return;
      }
    }

    const quantityButton = event.target.closest('[data-customizer-qty-minus], [data-customizer-qty-plus]');
    if (quantityButton) {
      const root = getRoot(quantityButton);
      const input = root ? root.querySelector('[data-customizer-quantity]') : null;
      if (root && input) {
        event.preventDefault();
        stopThemeHandlers(event);
        const nextValue = Math.max(1, (Number(input.value) || 1) + (quantityButton.matches('[data-customizer-qty-plus]') ? 1 : -1));
        input.value = String(nextValue);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        updateTotals(root);
        return;
      }
    }

    const pageQuantityButton = event.target.closest('.productView-quantity .btn-quantity, quantity-input .btn-quantity');
    if (pageQuantityButton && !getRoot(pageQuantityButton)) {
      scheduleQuantityFallback(pageQuantityButton);

      const productView = pageQuantityButton.closest('.productView, [id^="ProductSection-"]') || document;
      window.setTimeout(() => {
        const quantityInput = productView.querySelector('input[name="quantity"][data-price]');
        if (quantityInput) updateCustomizerForProductQuantity(quantityInput);
      }, 0);
    }

    const cartQuantityButton = event.target.closest('cart-update-quantity .btn-quantity, quickshop-update-quantity .btn-quantity');
    if (cartQuantityButton && !getRoot(cartQuantityButton)) {
      scheduleQuantityFallback(cartQuantityButton);
    }

    const pageVariantControl = event.target.closest('.swatch-label, .single-label, [data-variant-id], [data-with-one-option], .productView-options input, .productView-options select, .product-form__input input, .product-form__input select');
    if (pageVariantControl && !getRoot(pageVariantControl)) {
      const productView = pageVariantControl.closest('.productView, [id^="ProductSection-"]') || document;
      window.setTimeout(() => {
        const variantInput = productView.querySelector('form input[name="id"], form select[name="id"]');
        if (variantInput) updateCustomizerForProductVariant(variantInput);
      }, 50);
    }

    const tipButton = event.target.closest('[data-customizer-tip-toggle]');
    if (tipButton) {
      const root = getRoot(tipButton);
      const panel = tipButton.closest('[data-customizer-panel]');
      const controlledId = tipButton.getAttribute('aria-controls');
      const controlledTipBox = controlledId ? document.getElementById(controlledId) : null;
      const tipBox = controlledTipBox && root && root.contains(controlledTipBox)
        ? controlledTipBox
        : panel
          ? panel.querySelector('[data-customizer-tip-box]')
          : null;
      if (root && tipBox) {
        event.preventDefault();
        stopThemeHandlers(event);
        const willOpen = tipBox.hidden;
        tipBox.hidden = !willOpen;
        tipButton.setAttribute('aria-expanded', String(willOpen));
        tipBox.classList.toggle('is-open', willOpen);
        return;
      }
    }

    const clearFileButton = event.target.closest('[data-customizer-file-clear]');
    if (clearFileButton) {
      const root = getRoot(clearFileButton);
      const fileInput = root ? root.querySelector('[data-customizer-file]') : null;
      if (root && fileInput) {
        event.preventDefault();
        stopThemeHandlers(event);
        fileInput.value = '';
        updateFileState(root, fileInput);
        return;
      }
    }

    const engravingChip = event.target.closest('[data-customizer-engraving-chip]');
    if (engravingChip) {
      const root = getRoot(engravingChip);
      const textInput = root ? root.querySelector('[data-customizer-engraving-text]') : null;
      if (root && textInput) {
        event.preventDefault();
        stopThemeHandlers(event);
        textInput.value = engravingChip.dataset.customizerEngravingValue || engravingChip.textContent.trim();
        textInput.dispatchEvent(new Event('input', { bubbles: true }));
        textInput.focus();
        updateEngravingPreview(root);
        return;
      }
    }

    const submitButton = event.target.closest('[data-customizer-submit]');
    if (submitButton) {
      return;
    }
  }, true);

  document.addEventListener('pointerup', (event) => {
    if (handleStepButtonActivation(event)) return;
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (handleDetailsSummaryActivation(event)) return;
    if (handleStepButtonActivation(event)) return;
  }, true);

  ['mousedown', 'pointerdown', 'touchstart'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      const protectedControl = event.target.closest(PROTECTED_CONTROL_SELECTOR);
      if (!protectedControl) return;

      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      } else {
        event.stopPropagation();
      }
    }, true);
  });

  ['click', 'mousedown', 'mouseup', 'pointerdown', 'touchstart'].forEach((eventName) => {
    window.addEventListener(eventName, blockExternalOverlayNavigation, true);
  });

  ['mousedown', 'mouseup', 'pointerdown', 'touchstart'].forEach((eventName) => {
    document.addEventListener(eventName, blockOverlayNavigation, true);
  });

  ['dragenter', 'dragover'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      const dropzone = event.target.closest('[data-customizer-dropzone]');
      if (!dropzone) return;
      event.preventDefault();
      stopThemeHandlers(event);
      dropzone.classList.add('is-dragover');
    }, true);
  });

  ['dragleave', 'drop'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      const dropzone = event.target.closest('[data-customizer-dropzone]');
      if (!dropzone) return;
      event.preventDefault();
      stopThemeHandlers(event);
      dropzone.classList.remove('is-dragover');

      if (eventName !== 'drop' || !event.dataTransfer || !event.dataTransfer.files.length) return;

      const root = getRoot(dropzone);
      const fileInput = root ? root.querySelector('[data-customizer-file]') : null;
      if (!root || !fileInput) return;

      try {
        fileInput.files = event.dataTransfer.files;
      } catch (error) {
        return;
      }

      updateFileState(root, fileInput);
      fileInput.dispatchEvent(new Event('change', { bubbles: true }));
    }, true);
  });

  document.addEventListener('change', (event) => {
    const addon = event.target.closest('[data-customizer-addon]');
    if (addon) {
      const root = getRoot(addon);
      if (root) {
        syncAddon(root, addon);
        updateTotals(root);
      }
      return;
    }

    const variantChoice = event.target.closest('[data-customizer-variant-choice]');
    if (variantChoice) {
      const root = getRoot(variantChoice);
      if (root) selectCustomizerVariant(root, variantChoice.value);
      return;
    }

    const fileInput = event.target.closest('[data-customizer-file]');
    if (fileInput) {
      const root = getRoot(fileInput);
      updateFileState(root, fileInput);
    }

    const customizerQuantity = event.target.closest('[data-customizer-quantity]');
    if (customizerQuantity) {
      const root = getRoot(customizerQuantity);
      if (root) updateTotals(root);
      return;
    }

    const customizerSelect = event.target.closest(`${ROOT_SELECTOR} select`);
    if (customizerSelect) {
      const root = getRoot(customizerSelect);
      closeCustomizerSelect(customizerSelect);
      if (root) {
        if (customizerSelect.matches('[data-customizer-engraving-font], [data-customizer-engraving-placement]')) {
          updateEngravingPreview(root);
        } else {
          updateCustomizerProperties(root);
        }
      }
      return;
    }

    const productVariant = event.target.closest('input[name="id"], select[name="id"]');
    if (productVariant && !getRoot(productVariant)) {
      updateCustomizerForProductVariant(productVariant);
      return;
    }

    const productQuantity = event.target.closest('input[name="quantity"][data-price]');
    if (productQuantity) {
      updateThemeProductSubtotal(productQuantity);
      updateCustomizerForProductQuantity(productQuantity);
    }
  });

  document.addEventListener('input', (event) => {
    const engravingText = event.target.closest('[data-customizer-engraving-text]');
    if (engravingText) {
      const root = getRoot(engravingText);
      if (root) updateEngravingPreview(root);
      return;
    }

    const customizerQuantity = event.target.closest('[data-customizer-quantity]');
    if (customizerQuantity) {
      const root = getRoot(customizerQuantity);
      if (root) updateTotals(root);
      return;
    }

    const productVariant = event.target.closest('input[name="id"], select[name="id"]');
    if (productVariant && !getRoot(productVariant)) {
      updateCustomizerForProductVariant(productVariant);
      return;
    }

    const productQuantity = event.target.closest('input[name="quantity"][data-price]');
    if (productQuantity) {
      updateThemeProductSubtotal(productQuantity);
      updateCustomizerForProductQuantity(productQuantity);
    }
  });

  document.addEventListener('submit', (event) => {
    const root = event.target.matches(ROOT_SELECTOR) ? event.target : getRoot(event.target);
    if (!root) {
      const formCustomizer = customizerForProductForm(event.target);
      if (!formCustomizer) return;

      event.preventDefault();
      stopThemeHandlers(event);
      initializeRoot(formCustomizer);

      if (!requireUploadBeforeContinue(formCustomizer)) return;

      submitCustomizer(formCustomizer);

      return;
    }

    initializeRoot(root);

    const hasFileUpload = Array.from(root.querySelectorAll('input[type="file"]')).some((input) => input.files && input.files.length);
    if (!hasFileUpload && !requireUploadBeforeContinue(root)) {
      event.preventDefault();
      stopThemeHandlers(event);
      return;
    }

    event.preventDefault();
    stopThemeHandlers(event);
    submitCustomizer(root);
  }, true);

  const cartProperties = (item) => (item && item.properties && typeof item.properties === 'object' ? item.properties : {});

  const isBundledAddonItem = (item) => {
    const properties = cartProperties(item);
    return Boolean(properties['_Customization bundle'] && properties['_Bundled main product'] && properties['Add-on selection']);
  };

  const isBundledMainItem = (item, bundleId) => {
    const properties = cartProperties(item);
    return Boolean(properties['_Customization bundle'] === bundleId && !properties['_Bundled main product']);
  };

  const compactProperties = (properties, removedAddon = {}) => {
    const next = {};
    Object.entries(properties || {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        next[key] = value;
      }
    });

    [
      'Estimated add-ons',
      'Estimated add-on total',
      'Estimated order total',
      'Paid add-ons',
      'Paid add-ons total',
      'Customization notes',
    ].forEach((key) => {
      delete next[key];
    });

    const removedKind = removedAddon.kind || addonKind(removedAddon.selection || '', removedAddon.productTitle || '');

    if (removedKind === 'figures') {
      delete next['3D figures request'];
    }

    if (removedKind === 'warranty') {
      delete next['Warranty request'];
    }

    if (removedKind === 'light-base') {
      delete next['Light base request'];
    }

    return next;
  };

  const sameProperties = (left, right) => JSON.stringify(left || {}) === JSON.stringify(right || {});

  const cartRequest = async (url, payload) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });
    const parsed = await response.json().catch(() => ({}));

    if (!response.ok || parsed.errors || parsed.status) {
      throw new Error(parsed.description || parsed.message || parsed.errors || 'Cart could not be updated.');
    }

    return parsed;
  };

  const refreshCartInterface = (cart) => {
    if (!cart) return;

    if (document.body.classList.contains('template-cart') && window.halo?.updateCart) {
      window.halo.updateCart(cart);
      return;
    }

    if (document.body.classList.contains('cart-sidebar-show') && window.halo?.updateSidebarCart) {
      window.halo.updateSidebarCart(cart);
      return;
    }

    document.dispatchEvent(new CustomEvent('kristal:cart-synced', { detail: cart }));
  };

  let pendingCartSync = null;
  let cartSyncRunning = false;

  const rememberCartRemoval = (target) => {
    const button = target.closest?.('[data-cart-remove]');
    if (!button) return;

    const row = button.closest('[data-item-row], .previewCartItem');
    const bundle = button.dataset.kristalBundle || row?.dataset.kristalBundle || '';
    if (!bundle) return;

    const selection = button.dataset.kristalAddonSelection || row?.dataset.kristalAddonSelection || '';
    const bundledMain = button.dataset.kristalBundledMain || row?.dataset.kristalBundledMain || '';
    const productTitle = button.dataset.kristalAddonProduct || row?.dataset.kristalAddonProduct || '';

    pendingCartSync = {
      type: selection && bundledMain ? 'remove-addon' : 'remove-main',
      bundle,
      selection,
      productTitle,
      kind: button.dataset.kristalAddonKind || row?.dataset.kristalAddonKind || addonKind(selection, productTitle),
      createdAt: Date.now(),
    };
  };

  const syncRemovedAddon = async (pending, cart) => {
    const mainItem = cart.items.find((item) => isBundledMainItem(item, pending.bundle));
    if (!mainItem) return null;

    const nextProperties = compactProperties(cartProperties(mainItem), pending);
    if (sameProperties(nextProperties, cartProperties(mainItem))) return null;

    return cartRequest(window.routes?.cart_change_url || '/cart/change.js', {
      id: mainItem.key,
      quantity: mainItem.quantity,
      properties: nextProperties,
      sections_url: window.location.pathname,
    });
  };

  const removeOrphanedAddons = async (pending, cart) => {
    const updates = {};

    cart.items.forEach((item) => {
      const properties = cartProperties(item);
      if (properties['_Customization bundle'] === pending.bundle && isBundledAddonItem(item)) {
        updates[item.key] = 0;
      }
    });

    if (!Object.keys(updates).length) return null;

    return cartRequest(window.routes?.cart_update_url || '/cart/update.js', {
      updates,
      sections_url: window.location.pathname,
    });
  };

  document.addEventListener('click', (event) => {
    rememberCartRemoval(event.target);
  }, true);

  document.addEventListener('cart-update', async (event) => {
    if (cartSyncRunning || !pendingCartSync || !event.detail?.items) return;
    if (Date.now() - pendingCartSync.createdAt > 7000) {
      pendingCartSync = null;
      return;
    }

    const pending = pendingCartSync;
    pendingCartSync = null;
    cartSyncRunning = true;

    try {
      const syncedCart = pending.type === 'remove-addon'
        ? await syncRemovedAddon(pending, event.detail)
        : await removeOrphanedAddons(pending, event.detail);

      if (syncedCart) refreshCartInterface(syncedCart);
    } catch (error) {
      console.error('[kristal-cart-sync]', error);
    } finally {
      cartSyncRunning = false;
    }
  });

  document.addEventListener('shopify:section:load', initializeAll);
  window.addEventListener('pageshow', (event) => {
    if (isHistoryRestore(event)) {
      initializeAll();
      resetAllRoots();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll);
  } else {
    initializeAll();
  }
})();
